import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { cx, isFilled } from '@/shared/lib'
import s from './EmptyState.module.css'

/**
 * Плотность блока. `sm` — компакт внутри выпадающей панели (палитра ⌘K, список опций,
 * журнал действий), `md` — блок внутри панели или тела таблицы, `lg` — пустой экран целиком
 * (корзина, сравнение, витрина каталога).
 */
export type EmptyStateSize = 'sm' | 'md' | 'lg'

/** `start` — блок продолжает колонку выровненных по левому краю строк выпадающего списка. */
export type EmptyStateAlign = 'center' | 'start'

/** Уровень заголовка. Не задан — заголовок остаётся `div` и в план заголовков не попадает. */
export type EmptyStateHeadingLevel = 2 | 3 | 4 | 5 | 6

/**
 * Живость. Ось намеренно уже, чем у `Alert`: `assertive` у пустоты не бывает — она никогда
 * не перебивает начатое. По умолчанию блок молчит, областью владеет потребитель.
 */
export type EmptyStateAnnounce = 'off' | 'polite'

export type EmptyStateProps = {
  /**
   * Что произошло: «Под фильтры не попало ни одной строки».
   * Не `title`: то занято нативным тултипом и уходит в `...rest` — конвенция слайса,
   * та же причина, что у `heading` в `Alert` и `Card`.
   */
  heading?: ReactNode
  /** `h2`…`h6`. Уровень зависит от места вставки, поэтому выбирает его потребитель. */
  headingLevel?: EmptyStateHeadingLevel
  /** Абзац: чем пустота вызвана и что делать дальше. Переносится, а не обрезается. */
  children?: ReactNode
  /** Слот глифа: `<Icon name="filter" size="lg" />`. Плитку вокруг рисует компонент. */
  icon?: ReactNode
  /** Условия, давшие ноль: снимаемые `Chip`. Встают между абзацем и действиями. */
  filters?: ReactNode
  /** До трёх действий, первое — главный следующий шаг. До 480px становятся колонкой. */
  actions?: ReactNode
  size?: EmptyStateSize
  align?: EmptyStateAlign
  announce?: EmptyStateAnnounce
  ref?: Ref<HTMLDivElement>
} & Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'role'>

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const SIZE_CLASS: Record<EmptyStateSize, string | undefined> = {
  sm: s.sm,
  md: s.md,
  lg: s.lg,
}

const ALIGN_CLASS: Record<EmptyStateAlign, string | undefined> = {
  center: s.alignCenter,
  start: s.alignStart,
}

// Карта объявлена вне рендера: тег не создаётся заново на каждую отрисовку
const HEADING_TAG: Record<EmptyStateHeadingLevel, 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> = {
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
}

/** `status` встаёт в очередь и не перебивает речь. `alert` здесь не бывает — см. контракт. */
const ROLE: Record<EmptyStateAnnounce, 'status' | undefined> = {
  off: undefined,
  polite: 'status',
}

/**
 * Блок на месте данных, которых нет, — и следующий шаг вместо констатации.
 *
 * Пустота бывает четырёх видов («ничего не заведено», «под фильтры не попало»,
 * «ничего не нашлось», «нет прав») — они различаются содержимым слотов, а не пропом.
 * Ошибка загрузки сюда не заходит: данные, возможно, есть, шаг один — «Повторить»,
 * и объявляется он иначе. Её место — `Alert`.
 *
 * Своей подложки нет: материал даёт `Surface`/`Card` снаружи. Слота под произвольное
 * содержимое нет тоже — блок аналогов и плашка черновика из кита живут соседями.
 *
 * Контракт: docs/ui-kit/contracts/empty-state.md
 */
export function EmptyState({
  heading,
  headingLevel,
  children,
  icon,
  filters,
  actions,
  size = 'md',
  align = 'center',
  announce = 'off',
  className,
  ref,
  ...rest
}: EmptyStateProps) {
  // Слот приходит булевым от идиомы прав `can('order.create') && <Button/>` — отсюда isFilled
  const hasHeading = isFilled(heading)
  const hasText = isFilled(children)

  // Плитка с кнопками, но без единого слова — не сообщение, а мусор
  if (!hasHeading && !hasText) return null

  const Heading = headingLevel === undefined ? 'div' : HEADING_TAG[headingLevel]

  return (
    <div
      {...rest}
      ref={ref}
      className={cx(s.root, SIZE_CLASS[size], ALIGN_CLASS[align], className)}
      role={ROLE[announce]}
      data-size={size}
      data-align={align}
      data-announce={announce}
    >
      {/* Плитка декоративна всегда: глиф дублирует заголовок, а два объявления подряд — шум */}
      {isFilled(icon) ? (
        <span className={s.media} aria-hidden="true">
          {icon}
        </span>
      ) : null}

      {hasHeading ? <Heading className={s.heading}>{heading}</Heading> : null}

      {/*
       * Единственная строка вес не повышает, в отличие от Alert: там строка и есть сообщение
       * об условии, здесь — тихая сноска «тут ничего нет», и кричать ей нечем.
       */}
      {hasText ? <p className={s.text}>{children}</p> : null}

      {isFilled(filters) ? <div className={s.filters}>{filters}</div> : null}
      {isFilled(actions) ? <div className={s.actions}>{actions}</div> : null}
    </div>
  )
}
